interface PlanAction {
  actionType?: unknown
  startTime?: unknown
  endTime?: unknown
  targetValue?: unknown
  executionStatus?: unknown
}

const STATUS_COLOR: Record<string, string> = {
  executed: 'bg-emerald-500',
  failed: 'bg-red-400',
  overridden: 'bg-ink-dim/40',
  pending: 'bg-accent',
}

const HOUR_MARKS = [0, 6, 12, 18, 24]

function hourFraction(iso: string): number | null {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return null
  return d.getUTCHours() + d.getUTCMinutes() / 60
}

export function EnergyOSPlanTimeline({ plan, actions }: {
  plan?: { status?: unknown; generatedAt?: unknown }
  actions: PlanAction[]
}) {
  if (actions.length === 0) {
    return <p className="text-xs text-ink-dim text-center py-10">No planned actions yet</p>
  }

  return (
    <div>
      {plan?.status !== undefined && (
        <p className="text-theme-xs text-ink-dim mb-4">
          Plan status: <span className="font-medium text-ink-dim">{String(plan.status)}</span>
        </p>
      )}

      <div className="relative h-16 bg-paper-sunken rounded-lg border border-paper-border mt-4">
        {HOUR_MARKS.map(h => (
          <div key={h} className="absolute top-0 bottom-0 border-l border-paper-border" style={{ left: `${(h / 24) * 100}%` }}>
            <span className="absolute -top-4 -translate-x-1/2 text-[10px] text-ink-dim">{h}:00</span>
          </div>
        ))}
        {actions.map((a, i) => {
          const start = hourFraction(String(a.startTime ?? ''))
          if (start === null) return null
          const end = a.endTime ? hourFraction(String(a.endTime)) : null
          const left = (start / 24) * 100
          const width = Math.max((((end ?? start + 0.25) - start) / 24) * 100, 1.5)
          const status = String(a.executionStatus ?? '')
          const color = STATUS_COLOR[status] ?? "bg-ink-dim/40"
          return (
            <div
              key={i}
              className={`absolute top-4 h-8 rounded ${color} ${status === 'overridden' ? 'opacity-50' : ''} flex items-center justify-center overflow-hidden`}
              style={{ left: `${left}%`, width: `${width}%` }}
              title={`${a.actionType ?? ''} ${a.targetValue ?? ''} — ${status}`}
            >
              <span className="text-[9px] font-medium text-white truncate px-1">{String(a.actionType ?? '')}</span>
            </div>
          )
        })}
      </div>

      <div className="flex flex-wrap gap-3 mt-3 text-[10px] text-ink-dim">
        <Legend color="bg-emerald-500" label="Executed" />
        <Legend color="bg-red-400" label="Failed" />
        <Legend color="bg-ink-dim/40" label="Overridden" />
      </div>
    </div>
  )
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1">
      <span className={`w-2 h-2 rounded-full ${color}`} />
      {label}
    </span>
  )
}
